import {Injectable, NgZone} from '@angular/core';
import {environment} from '../../environments/environment';

// Form effort and task sequences: an independent add-on to the usage stats (UsageTrackerService), groundwork for
// spotting work an AI could take over later. Its events go into the same report (user_stats_events_daily):
//
//   form_save    page | "<METHOD> <api path>|<duration>"  a form was edited and saved (the write it ended with)
//   form_field   page | "<METHOD> <api path>|<field>"     a field edited in a saved form (once per save)
//   form_abandon page | "modal|<duration>" / "page|…"     a form was edited but not saved (modal closed / page left)
//   page_flow    page | "<next page pattern>"             one step from this page to the next (same tab)
//   form_rejected page | "<METHOD> <api path>|<outcome>"   a form whose save was rejected (400/409/422), once it ends:
//                                                         saved-1 / saved-2+ (fixed and saved after 1 / 2+ rejections)
//                                                         or abandoned (given up)
//
// Durations are buckets (first edit → save / abandon): <15s, 15-60s, 1-3m, 3-10m, 10m+.
//
// Privacy: values are NEVER read (no .value / .checked / .files / clipboard). From an edit only the field's attributes
// (formcontrolname, name, id, type, autocomplete) are looked at, for its technical name. Custom controls without native
// input events (app-live-search-select, app-date-picker…): a click inside a [formcontrolname] host counts as an edit once
// Angular has marked it ng-dirty (its value changed through the UI); only that class name is read. Sensitive fields (passwords,
// security answers, keys, tokens, card / bank numbers…) are skipped entirely, and the backend rejects their names again
// (EVENT_KEYS in backend/utils/usageStats.js). Forms are identified by the API route they write to (ids → ':id'), not by
// titles, which can contain record names. Rejections use only the status and the route: response bodies are never read
// (their messages are free text with values in them).
//
// Switches: config/usage_tracking.js spa.forms, spa.flows and spa.rejections, independent of each other and of the other
// trackers. Off → no listeners at all (forms and rejections share the form listeners), and the backend drops these
// events. Every entry point catches its own errors; after MAX_ERRORS errors this service stops itself — page, filter
// and section tracking carry on. Rejections have their own guard (safelyRejections): their errors only turn rejections
// off, never the form and flow tracking.
//
// Remove completely: delete this file; remove the lines marked "form & flow tracking" in usage-tracker.service.ts and
// the recordApiWrite call in interceptors/frontend-error.interceptor.ts; remove form_save, form_field, form_abandon and
// page_flow, form_rejected from EVENT_SWITCHES / EVENT_KEYS in backend/utils/usageStats.js and the `forms` / `flows` /
// `rejections` switches (config/usage_tracking.js, utils/usageTrackingSwitches.js); optionally
// DELETE FROM user_stats_events_daily WHERE event IN ('form_save', 'form_field', 'form_abandon', 'page_flow', 'form_rejected').
// Rejections only: remove recordApiWriteRejected (and its call in the interceptor), safelyRejections, rejectionOutcome
// and the `rejections` fields, then the form_rejected event and the `rejections` switch.

export type FormFlowEvent = 'form_save' | 'form_field' | 'form_abandon' | 'page_flow' | 'form_rejected';
export interface FormFlowSwitches {
    forms: boolean;
    flows: boolean;
    rejections: boolean;
}
type AddEvent = (event: FormFlowEvent, key: string, page: string) => void;

const MAX_ERRORS = 5;
const MAX_FIELDS_PER_SAVE = 20;
const CHECK_MS = 3000;
// A step older than this is a new visit, not part of a task
const FLOW_GAP_MS = 30 * 60 * 1000;
// A form nobody touched for this long is given up
const STALE_SESSION_MS = 30 * 60 * 1000;
const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
// A save the server refused (validation and the like; Hexagon uses all three for it)
const REJECTED_STATUSES = [400, 409, 422];
const FIELD_NAME = /^[A-Za-z0-9_\-.]{1,60}$/;
// Never tracked, not even their name (same list as SENSITIVE_FIELD in backend/utils/usageStats.js)
const SENSITIVE_FIELD = /pass|pwd|secret|token|key|answer|security|pin|otp|iban|card|cvv|cvc|recovery|credential/i;
const SENSITIVE_TYPES = ['password', 'hidden', 'file'];
const IGNORED_AREAS = '[data-usage-ignore]';
const MODAL = '.modal-content';

interface FormSession {
    page: string;
    container: Element | null;   // the modal, or null for a form on the page itself
    started: number;
    lastEdit: number;
    fields: Set<string>;
    rejections: number;           // saves the server refused while this form was open
    rejectedRoute: string | null; // the last refused write, "<METHOD> <api path>"
    edited: boolean;              // false = opened by a refused save only (edits made in ways not seen): no form_save / form_abandon
}

@Injectable({providedIn: 'root'})
export class UsageFormFlowTrackerService {
    private switches: FormFlowSwitches = {forms: false, flows: false, rejections: false};
    private addEvent: AddEvent | null = null;
    private stopped = false;
    private errors = 0;
    // Rejections: own error counter, so their errors never stop the form and flow tracking
    private rejectionsOff = false;
    private rejectionErrors = 0;
    private sessions: FormSession[] = [];
    private lastNavigation = 0;
    private checkTimer: ReturnType<typeof setInterval> | null = null;
    private currentPage: string | null = null;
    private readonly onEdit = (event: Event) => this.safely(() => {
        const target = event.target;
        if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) this.edited(target);
    });
    private readonly onClick = (event: Event) => this.safely(() => this.clicked(event.target));
    private readonly onPageHide = () => this.safely(() => this.abandonAll());

    constructor(private zone: NgZone) {}

    // Called once by UsageTrackerService with the switches from GET /api/status and its event recorder
    start(switches: Partial<FormFlowSwitches> | null | undefined, addEvent: AddEvent) {
        this.safely(() => {
            this.switches = {forms: switches?.forms !== false, flows: switches?.flows !== false, rejections: switches?.rejections !== false};
            if (!this.switches.forms && !this.switches.flows && !this.switches.rejections) return;
            this.addEvent = addEvent;
            // Rejections need the form sessions too
            if (this.sessionsOn) {
                this.zone.runOutsideAngular(() => {
                    ['input', 'change'].forEach(name => document.addEventListener(name, this.onEdit, {passive: true, capture: true}));
                    document.addEventListener('click', this.onClick, {passive: true, capture: true});
                    window.addEventListener('pagehide', this.onPageHide);
                    this.checkTimer = setInterval(() => this.safely(() => this.checkSessions()), CHECK_MS);
                });
            }
        });
    }

    // A page view (UsageTrackerService.onNavigation): previous and new page pattern, null = untracked page
    onNavigation(previousPage: string | null, page: string | null) {
        this.safely(() => {
            const now = Date.now();
            if (this.switches.flows && previousPage && page && previousPage !== page && now - this.lastNavigation < FLOW_GAP_MS) {
                this.record('page_flow', page, previousPage);
            }
            this.lastNavigation = now;
            this.currentPage = page;
            // Forms left behind on another page are given up
            if (this.sessionsOn) this.sessions.filter(session => session.page !== page).forEach(session => this.abandon(session));
        });
    }

    // A successful API write (from FrontendErrorInterceptor): the form edited last on this page was saved by it
    recordApiWrite(method: string, url: string) {
        if (this.stopped || !this.sessionsOn || !WRITE_METHODS.includes(String(method).toUpperCase())) return;
        this.safely(() => {
            const session = this.newestSession();
            if (!session) return;
            const route = this.route(method, url);
            this.sessions = this.sessions.filter(item => item !== session);
            if (this.switches.forms && session.edited) {
                this.record('form_save', `${route}|${this.bucket(Date.now() - session.started)}`, session.page);
                [...session.fields].slice(0, MAX_FIELDS_PER_SAVE).forEach(field => this.record('form_field', `${route}|${field}`, session.page));
            }
            // Last, in its own guard: never affects the form events above
            this.rejectionOutcome(session, session.rejections > 1 ? 'saved-2+' : 'saved-1');
        });
    }

    // A write refused with 400 / 409 / 422 (from FrontendErrorInterceptor): the form edited last on this page stays open
    // and remembers it. Only the status and the route are used; the response body is never read.
    recordApiWriteRejected(method: string, url: string, status: number) {
        if (this.stopped || this.rejectionsOff || !this.switches.rejections || !REJECTED_STATUSES.includes(status)
            || !WRITE_METHODS.includes(String(method).toUpperCase())) return;
        this.safelyRejections(() => {
            let session = this.newestSession();
            // Edits made only by clicks the tracker doesn't see: the refused save itself starts the form (outcome only)
            if (!session && this.currentPage) {
                const now = Date.now();
                session = {page: this.currentPage, container: null, started: now, lastEdit: now, fields: new Set<string>(),
                    rejections: 0, rejectedRoute: null, edited: false};
                this.sessions.push(session);
            }
            if (!session) return;
            session.rejections++;
            session.rejectedRoute = this.route(method, url);
        });
    }

    private get sessionsOn(): boolean {
        return this.switches.forms || this.switches.rejections;
    }

    private newestSession(): FormSession | undefined {
        return this.sessions.filter(item => item.page === this.currentPage).sort((a, b) => b.lastEdit - a.lastEdit)[0];
    }

    // "<METHOD> <api path>", ids → ':id'. Fixed base: only the path is used ('api/x' → '/api/x'), and it can't fail on
    // an unusual origin
    private route(method: string, url: string): string {
        const path = new URL(url, 'http://localhost/').pathname.replace(/\/\d+(?=\/|$)/g, '/:id').slice(0, 150);
        return `${String(method).toUpperCase()} ${path}`;
    }

    // A click inside a custom form control (a [formcontrolname] host that isn't a native field): an edit once Angular has
    // marked the control ng-dirty, checked on the next tick (after the control applied the click)
    private clicked(target: EventTarget | null) {
        if (!(target instanceof Element)) return;
        const host = target.closest('[formcontrolname]');
        if (!host || host instanceof HTMLInputElement || host instanceof HTMLSelectElement || host instanceof HTMLTextAreaElement) return;
        setTimeout(() => this.safely(() => {
            if (host.isConnected && host.classList.contains('ng-dirty')) this.edited(host);
        }), 0);
    }

    // An edit somewhere on the page: open (or continue) the session of its form. Only attributes are read.
    private edited(target: Element) {
        if (!this.currentPage) return;
        if (target.closest(IGNORED_AREAS) || this.isSensitive(target) || this.isFilterField(target)) return;
        const container = target.closest(MODAL);
        // Edits outside a form or a modal (list checkboxes, inline toggles) aren't form work
        if (!container && !target.closest('form')) return;

        const now = Date.now();
        let session = this.sessions.find(item => item.page === this.currentPage && item.container === container);
        if (!session) {
            session = {page: this.currentPage, container, started: now, lastEdit: now, fields: new Set<string>(), rejections: 0, rejectedRoute: null, edited: true};
            this.sessions.push(session);
        }
        session.lastEdit = now;
        session.edited = true;
        const field = this.fieldName(target);
        if (field) session.fields.add(field);
    }

    private isSensitive(el: Element): boolean {
        const type = (el.getAttribute('type') || '').toLowerCase();
        const autocomplete = (el.getAttribute('autocomplete') || '').toLowerCase();
        if (SENSITIVE_TYPES.includes(type) || /password|one-time-code|cc-/.test(autocomplete)) return true;
        return ['formcontrolname', 'name', 'id'].some(attr => SENSITIVE_FIELD.test(el.getAttribute(attr) || ''));
    }

    // Search boxes and filter forms (a form with a 'search' field or a field in the URL) are filtering, not form work
    private isFilterField(el: Element): boolean {
        if ((el.getAttribute('type') || '').toLowerCase() === 'search') return true;
        const form = el.closest('form');
        if (!form || el.closest(MODAL)) return false;
        const inUrl = new URLSearchParams(location.search);
        return Array.from(form.querySelectorAll('[formcontrolname]'))
            .some(field => {
                const name = field.getAttribute('formcontrolname') || '';
                return name === 'search' || inUrl.has(name);
            });
    }

    // The field's technical name (formcontrolname → name → id); never its value
    private fieldName(el: Element): string | null {
        const name = el.getAttribute('formcontrolname') || el.getAttribute('name') || el.getAttribute('id') || '';
        return FIELD_NAME.test(name) && !SENSITIVE_FIELD.test(name) ? name : null;
    }

    // Modals closed without a save, and forms left untouched for too long, are given up
    private checkSessions() {
        const now = Date.now();
        this.sessions
            .filter(session => (session.container && !session.container.isConnected) || now - session.lastEdit > STALE_SESSION_MS)
            .forEach(session => this.abandon(session));
    }

    private abandonAll() {
        [...this.sessions].forEach(session => this.abandon(session));
    }

    private abandon(session: FormSession) {
        this.sessions = this.sessions.filter(item => item !== session);
        if (this.switches.forms && session.edited) {
            this.record('form_abandon', `${session.container ? 'modal' : 'page'}|${this.bucket(session.lastEdit - session.started)}`, session.page);
        }
        // Last, in its own guard: never affects the form event above
        this.rejectionOutcome(session, 'abandoned');
    }

    // A form with refused saves ended: fixed and saved, or given up
    private rejectionOutcome(session: FormSession, outcome: 'saved-1' | 'saved-2+' | 'abandoned') {
        this.safelyRejections(() => {
            if (!this.switches.rejections || !session.rejections || !session.rejectedRoute) return;
            this.record('form_rejected', `${session.rejectedRoute}|${outcome}`, session.page);
        });
    }

    private bucket(ms: number): string {
        const seconds = ms / 1000;
        if (seconds < 15) return '<15s';
        if (seconds < 60) return '15-60s';
        if (seconds < 180) return '1-3m';
        if (seconds < 600) return '3-10m';
        return '10m+';
    }

    private record(event: FormFlowEvent, key: string, page: string) {
        if (this.addEvent && page) this.addEvent(event, key, page);
    }

    private stop() {
        this.stopped = true;
        ['input', 'change'].forEach(name => document.removeEventListener(name, this.onEdit, true));
        document.removeEventListener('click', this.onClick, true);
        window.removeEventListener('pagehide', this.onPageHide);
        if (this.checkTimer) clearInterval(this.checkTimer);
        this.sessions = [];
        if (!environment.production) console.warn('UsageFormFlowTracker: turned off after repeated errors');
    }

    // Rejections only: errors never reach the app nor the other steps; after MAX_ERRORS only rejections turn off
    private safelyRejections(step: () => void) {
        if (this.stopped || this.rejectionsOff) return;
        try {
            step();
        } catch (e) {
            if (!environment.production) console.warn('UsageFormFlowTracker (rejections):', e);
            if (++this.rejectionErrors >= MAX_ERRORS) this.rejectionsOff = true;
        }
    }

    // Runs a step; errors never reach the app, and after MAX_ERRORS this service turns itself off
    private safely(step: () => void) {
        if (this.stopped) return;
        try {
            step();
        } catch (e) {
            if (!environment.production) console.warn('UsageFormFlowTracker:', e);
            if (++this.errors >= MAX_ERRORS) {
                try {
                    this.stop();
                } catch {
                    // already off
                }
            }
        }
    }
}
